import { useRef, useState } from "react";
import html2canvas from "html2canvas";

function tradeNet(t) {
  return (Number(t.pnl) || 0) - (Number(t.fees) || 0) - (Number(t.commissions) || 0);
}

function TradeCard({ trade, tag, note, wr }) {
  const isWin = trade.net >= 0;
  const sign = trade.net >= 0 ? "+" : "";
  const ptSign = trade.pts >= 0 ? "+" : "";

  return (
    <div style={{
      background: "#111214", borderRadius: 10, padding: 20,
      border: `1px solid ${isWin ? "#1a4731" : "#3a1a1a"}`,
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      width: 320,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <span style={{ fontSize: 10, color: "#6b7280", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          Trade result
        </span>
        {tag && (
          <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: "#1a2d3a", color: "#60b8d4", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {tag.toUpperCase()}
          </span>
        )}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: "#fff" }}>{trade.symbol}</div>
      <div style={{ fontSize: 10, color: "#4b5563", marginBottom: 10 }}>{trade.date} · {trade.time}</div>
      <span style={{
        display: "inline-block", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, marginBottom: 10,
        background: trade.dir === "LONG" ? "#0d2b1f" : "#2b0d0d",
        color: trade.dir === "LONG" ? "#34d399" : "#f87171",
      }}>{trade.dir}</span>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 10 }}>
        {[["Entry", trade.entry], ["Exit", trade.exit], ["Qty", `${trade.qty}x`]].map(([l, v]) => (
          <div key={l} style={{ background: "#1a1d23", borderRadius: 6, padding: "7px 8px" }}>
            <div style={{ fontSize: 9, color: "#6b7280" }}>{l}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#e5e7eb" }}>{v}</div>
          </div>
        ))}
      </div>
      <div style={{ background: isWin ? "#0d2b1f" : "#2b0d0d", borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
        <span style={{ fontSize: 24, fontWeight: 700, color: isWin ? "#34d399" : "#f87171" }}>
          {sign}${Math.abs(trade.net).toFixed(2)}
        </span>
        <span style={{ fontSize: 12, color: isWin ? "#34d399" : "#f87171", opacity: 0.8, marginLeft: 6 }}>
          {ptSign}{trade.pts.toFixed(2)} pts
        </span>
      </div>
      {note && <div style={{ fontSize: 10, color: "#6b7280", fontStyle: "italic", marginBottom: 10 }}>"{note}"</div>}
      <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #1f2128", paddingTop: 8 }}>
        <span style={{ fontSize: 9, color: "#374151" }}>futures-os.app</span>
        {wr != null && !isNaN(wr) && (
          <span style={{ fontSize: 10, fontWeight: 600, color: "#a3e635" }}>W% {Number(wr).toFixed(1)}</span>
        )}
      </div>
    </div>
  );
}

export function TradeShareModal({ trade, onClose, allTrades = [] }) {
  const cardRef = useRef(null);
  const [tag, setTag] = useState(trade.setup_type || "");
  const [note, setNote] = useState("");
  const [copyState, setCopyState] = useState(null);

  const net = tradeNet(trade);
  const pts = (Number(trade.exit_price) || 0) - (Number(trade.entry_price) || 0);
  const adjustedPts = trade.direction === "Short" ? -pts : pts;

  const wins = allTrades.filter(t => tradeNet(t) > 0).length;
  const wr = allTrades.length > 0 ? (wins / allTrades.length) * 100 : null;

  const now = new Date();
  const tradeDate = trade.trade_date || trade.created_at?.split("T")[0] || now.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const entryTime = (trade.entry_time || trade.trade_time || "")?.slice(0, 8) || now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
  const exitTime = (trade.exit_time || "")?.slice(0, 8);

  // Format date nicely
  const formattedDate = (() => {
    try {
      const d = new Date(tradeDate + "T12:00:00");
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    } catch { return tradeDate; }
  })();

  const builtTrade = {
    symbol: trade.symbol || "—",
    dir: trade.direction === "Long" ? "LONG" : "SHORT",
    entry: trade.entry_price || "—",
    exit: trade.exit_price || "—",
    qty: trade.position_size || trade.quantity || trade.size || 1,
    net,
    pts: adjustedPts,
    date: formattedDate,
    time: exitTime ? `${entryTime} – ${exitTime}` : entryTime,
  };

  const screenshotCard = async () => {
    if (!cardRef.current) return;
    setCopyState("img");
    try {
      const canvas = await html2canvas(cardRef.current, { backgroundColor: "#111214", scale: 2 });
      canvas.toBlob((blob) => {
        const item = new ClipboardItem({ "image/png": blob });
        navigator.clipboard.write([item]).then(() => setTimeout(() => setCopyState(null), 2200));
      });
    } catch { setCopyState(null); }
  };

  const copyText = () => {
    const sign = net >= 0 ? "+" : "";
    const ptSign = adjustedPts >= 0 ? "+" : "";
    const text = [
      `**${builtTrade.symbol} ${builtTrade.dir}**${tag ? ` · ${tag}` : ""}`,
      `Entry: ${builtTrade.entry} → Exit: ${builtTrade.exit} (${ptSign}${adjustedPts.toFixed(2)} pts)`,
      `Qty: ${builtTrade.qty} · Net P&L: ${sign}$${Math.abs(net).toFixed(2)}`,
      note ? `"${note}"` : null,
      wr != null ? `W%: ${wr.toFixed(1)} | futures-os.app` : "futures-os.app",
    ].filter(Boolean).join("\n");
    navigator.clipboard.writeText(text).then(() => {
      setCopyState("text");
      setTimeout(() => setCopyState(null), 2200);
    });
  };

  const fi = { background: "#1a1d23", border: "1px solid #1f2128", borderRadius: 6, height: 32, padding: "0 10px", fontSize: 12, color: "#e5e7eb", width: "100%", outline: "none" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#111317", border: "1px solid #1f2128", borderRadius: 12, padding: 24, display: "flex", gap: 24, maxWidth: 680, width: "100%", margin: "0 16px" }}>

        {/* Left — options */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#e5e7eb", marginBottom: 4 }}>Share trade</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 11, color: "#6b7280" }}>Setup tag</label>
            <input style={fi} value={tag} onChange={e => setTag(e.target.value)} placeholder="e.g. Gold Pattern, VWAP bounce…" />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 11, color: "#6b7280" }}>Note (optional)</label>
            <input style={fi} value={note} onChange={e => setNote(e.target.value)} placeholder="Clean entry, held through noise…" />
          </div>

          {/* P&L summary */}
          <div style={{ background: "#1a1d23", borderRadius: 8, padding: "10px 12px", marginTop: 4 }}>
            <div style={{ fontSize: 10, color: "#6b7280", marginBottom: 4 }}>Net P&L (after fees)</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: net >= 0 ? "#34d399" : "#f87171" }}>
              {net >= 0 ? "+" : ""}${Math.abs(net).toFixed(2)}
            </div>
            {(trade.fees || trade.commissions) ? (
              <div style={{ fontSize: 10, color: "#4b5563", marginTop: 2 }}>
                Fees: ${((Number(trade.fees) || 0) + (Number(trade.commissions) || 0)).toFixed(2)} deducted
              </div>
            ) : null}
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <button onClick={screenshotCard} style={{ flex: 1, background: copyState === "img" ? "#0d2b1f" : "#1e2d4a", border: `1px solid ${copyState === "img" ? "#4caf7d" : "#5b9cf6"}`, borderRadius: 6, padding: 8, fontSize: 12, fontWeight: 600, color: copyState === "img" ? "#4caf7d" : "#5b9cf6", cursor: "pointer" }}>
              {copyState === "img" ? "Copied!" : "Copy for Discord"}
            </button>
            <button onClick={copyText} style={{ flex: 1, background: "#1a1d23", border: `1px solid ${copyState === "text" ? "#4caf7d" : "#1f2128"}`, borderRadius: 6, padding: 8, fontSize: 12, color: copyState === "text" ? "#4caf7d" : "#9ca3af", cursor: "pointer" }}>
              {copyState === "text" ? "Copied!" : "Text only"}
            </button>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#4b5563", fontSize: 12, cursor: "pointer", marginTop: 4 }}>Cancel</button>
        </div>

        {/* Right — card preview */}
        <div ref={cardRef}>
          <TradeCard trade={builtTrade} tag={tag} note={note} wr={wr} />
        </div>
      </div>
    </div>
  );
}
